import React, { useEffect, useId, useRef } from 'react';
import './ModalDrawer.css';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  openerRef: React.RefObject<HTMLElement>;
  title: string;
  children: React.ReactNode;
};

const ModalDrawer: React.FC<Props> = ({ open, onOpenChange, openerRef, title, children }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    returnFocusRef.current = openerRef.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    closeRef.current?.focus();
    const keepFocusInside = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChange(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', keepFocusInside);
    return () => {
      document.removeEventListener('keydown', keepFocusInside);
      document.body.style.overflow = overflow;
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      returnFocusRef.current?.focus();
    };
  }, [onOpenChange, open, openerRef]);
  return <dialog ref={dialogRef} className="zh-modal-drawer" aria-labelledby={titleId} onCancel={event => { event.preventDefault(); onOpenChange(false); }} onClick={event => { if (event.target === event.currentTarget) onOpenChange(false); }}>
    <div className="zh-modal-drawer__panel">
      <header><strong id={titleId}>{title}</strong><button ref={closeRef} type="button" onClick={() => onOpenChange(false)}>Close menu</button></header>
      <div className="zh-modal-drawer__content">{children}</div>
    </div>
  </dialog>;
};

export default ModalDrawer;
